import { ActionMenu, BasicTable, FilterMenu, Notification, PageView, PageTopBar, SlidePanel, FormattedDate } from '../../components'
import { useQuery } from 'react-query'
import { useContext, useRef, useState } from 'react'
import { Loader } from '../../components/Loader'
import { UserContext } from '../../hooks/UserContext'
import { fetchWarehouses } from '../../services/warehouseSevices'
import { ROOT } from '../../navigation/constants'
import { getWarehouseRedirectPath } from '../../utils/warehouseRedirect'
import { Redirect, Route, Switch, useHistory } from 'react-router-dom'
import { setWarehousesListPageSize } from '../../redux/pageSizeSlice'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isSuperAdminUser } from '../../utils/userUtils'

export const ListWarehousesContainer = () => {
  const history = useHistory()
  const { user, setUser } = useContext(UserContext)
  const { i18n } = useTranslation()
  const [showDefaultWarehouseSetNotif, setShowDefaultWarehouseSetNotif] = useState(false)

  const isSuperAdmin = isSuperAdminUser(user)

  // No access to any warehouse. Do NOT navigate from here: calling
  // history.replace during render makes Router setState mid-render, which React
  // answers with "Maximum update depth exceeded" and an infinite redirect loop.
  // That loop also fires concurrent token refreshes, and with refresh-token
  // rotation Auth0 reads concurrent refreshes as reuse and revokes the whole
  // family — i.e. it logs the operator out everywhere. The redirect is returned
  // declaratively below, after every hook has run.
  const hasNoWarehouseAccess = Boolean(user && !user.read_all_warehouses && !isSuperAdmin && (!user.warehouse_ids || user.warehouse_ids.length === 0))

  const dispatch = useDispatch()
  const pageSize = useSelector(state => state.pageSize.warehousesList)

  const columns = [
    {
      accessor: 'name',
      Header: i18n.t('list_warehouses.headers.warehouse'),
      disableFilters: false,
      disableSortBy: false,
      searchAs: 'name',
    },
    {
      accessor: 'address',
      Header: i18n.t('list_warehouses.headers.address'),
      disableFilters: true,
      disableSortBy: true,
      shrinkToContent: true,
    },
    {
      accessor: 'state',
      Header: 'Estado',
      disableFilters: true,
      disableSortBy: true,
      searchAs: 'account_name',
    },
    {
      accessor: 'country',
      Header: i18n.t('list_warehouses.headers.country'),
      disableFilters: false,
      disableSortBy: true,
      searchAs: 'account_name',
    },
    {
      accessor: 'created_at',
      Header: i18n.t('list_warehouses.headers.created_at'),
      disableFilters: true,
      disableSortBy: false,
    },
    {
      accessor: 'actions',
      Header: '',
      fetchingIndicator: true,
      disableFilters: true,
      disableSortBy: true,
      shrinkToContent: true,
    },
  ]
  // omit warehouse_ids param if user can read all warehouses or is super admin
  const [searchParams, setSearchParams] = useState({
    page: 1,
    per_page: pageSize,
    count_products: true,
    ...(!user?.read_all_warehouses && !isSuperAdmin && { warehouse_ids: user?.warehouse_ids }),
  })

  const tableRef = useRef()

  const { isLoading, isError, error, data, isFetching, isPreviousData } = useQuery(['warehouses', searchParams], () => fetchWarehouses(searchParams), {
    keepPreviousData: true,
  })

  // Placed after every hook: an early return above them would change the hook
  // order between renders.
  if (hasNoWarehouseAccess) return <Redirect to="/" />

  const onSortChange = orderBy => {
    // console.log("ON_ORDER_BY",orderBy)
    if (orderBy.length > 0) {
      setSearchParams({
        ...searchParams,
        order_by: orderBy[0].id,
        order: orderBy[0].desc ? 'desc' : 'asc',
      })
    } else {
      if ('order_by' in searchParams) {
        delete searchParams.order_by
        delete searchParams.order
        setSearchParams({ ...searchParams })
      }
    }
  }

  const onFilterChange = activeFilters => {
    columns.forEach(col => {
      if (searchParams[col.searchAs]) {
        delete searchParams[col.searchAs]
      }
    })
    const filters = []
    activeFilters.forEach(filter => {
      const column = columns.find(col => col.accessor === filter.id)
      filters[column.searchAs] = filter.value
    })

    tableRef.current.resetPagination()
    setSearchParams({ ...searchParams, ...filters, page: 1 })
    // console.log("searchParams",searchParams)
  }

  const getMenuOptions = warehouse => {
    const options = [
      {
        title: 'Ir al Almacen',
        clickHandler: () => {
          onOpenWarehouseClick({ ...warehouse })
        },
      },
      {
        title: 'Ir Dashboard',
        clickHandler: () => {
          onOpenDashboardClick({ ...warehouse })
        },
      },
      {
        title: 'Ver detalle',
        clickHandler: () => {
          onDetailClick(warehouse.id)
        },
      },
    ]
    return options
  }
  const changeLanguage = country => {
    if (country === 'BR') {
      i18n.changeLanguage('pt')
    } else {
      i18n.changeLanguage('es')
    }
  }
  const onOpenWarehouse = warehouse => {
    onOpenWarehouseClick({ ...warehouse })
    changeLanguage(warehouse.country)
  }
  const getTableData = () => {
    return data.warehouses.map(warehouse => {
      return {
        ...warehouse,
        name: (
          <div
            className="hover:underline cursor-pointer"
            onClick={() => {
              onOpenWarehouse(warehouse)
            }}
          >
            {String(warehouse.name) + (user?.current_warehouse?.id === warehouse.id ? '*' : '')}
          </div>
        ),
        address: warehouse.address1,
        state: warehouse.state,
        country: warehouse.country,
        created_at: <FormattedDate date={warehouse.created_at} shortDate />,
        // created_at: new Date(warehouse.created_at),
        actions: <ActionMenu className="float-right" items={getMenuOptions(warehouse)} />,
      }
    })
  }

  const onDetailClick = warehouseId => {
    //history.push(STORE_DETAIL.replace(':id', warehouseId))
  }

  const onOpenWarehouseClick = warehouse => {
    setUser({ ...user, current_warehouse: warehouse })
    const path = getWarehouseRedirectPath(user, warehouse.id)
    history.push(path)
  }
  const onOpenDashboardClick = warehouse => {
    setUser({ ...user, current_warehouse: warehouse })
    history.push(ROOT.replace(':warehouse_id', warehouse.id))
  }

  return (
    <>
      <Notification
        show={showDefaultWarehouseSetNotif}
        setShow={setShowDefaultWarehouseSetNotif}
        type="success"
        title="Guardado"
        description="Se ha guardado tu preferencia de tienda predeterminada."
      />
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">{i18n.t('list_warehouses.warehouses')}</div>
          </PageTopBar>
        }
        childrenFullWidth={true}
        topMenuFullWidth={true}
      >
        {!isPreviousData && isLoading ? (
          <>
            <Loader show={true}></Loader>
          </>
        ) : isError ? (
          <>Error: {error.message}</>
        ) : (
          <BasicTable
            onFilterChange={onFilterChange}
            onSortChange={onSortChange}
            columns={columns}
            showPaginator
            showHeader
            filterable
            sortable
            paginationMeta={data?.meta}
            showCount={true}
            labelToPaginator={i18n.t('list_warehouses.warehouses')}
            pagesSize={searchParams.per_page}
            onPaginationChange={requestedPage => {
              setSearchParams({ ...searchParams, page: requestedPage })
            }}
            onPageSizeChange={pageSize => {
              setSearchParams({ ...searchParams, per_page: pageSize, page: 1 })
              dispatch(setWarehousesListPageSize(pageSize))
              tableRef.current.resetPagination()
            }}
            showLoader={isFetching && isPreviousData}
            isFetching={isFetching}
            data={getTableData()}

            emptyTableText={i18n.t('list_warehouses.empty_table_text')}
            ref={tableRef}
          />
        )}
      </PageView>
    </>
  )
}
